import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { IncomesIcon } from './icons/incomes-icon';
import { DashboardIcon } from './icons/dashboard-icon';
import { ExpendituresIcon } from './icons/expenditures-icon';
import { PersonIcon } from './icons/person-icon';
import { PetIcon } from './icons/pet-icon';
import { VehicleIcon } from './icons/vehicle-icon';
import { PropertyIcon } from './icons/property-icon';
import { ItemIcon } from './icons/item-icon';
import { SavingsIcon } from './icons/savings-icon';
import { ProtectionIcon } from './icons/protection-icon';
import { LiabilityIcon } from './icons/liability-icon';
import { SettingsIcon } from './icons/settings-icon';
import { DebugIcon } from './icons/debug-icon';
import { VaultSwitcher } from './vault-switcher';
import { VaultPasswordDialog } from './vault-password-dialog';
import { useAppDispatch, useAppSelector } from '@/stores/hooks';
import { readVaultThunk } from '@/stores/thunks/read-vault-thunk';
import cryptographerService from '../services/cryptographer-service';
import { useDebugMode } from '@/hooks/use-debug-mode';
import { VaultDocument, VaultSettingsNode } from '@syfo/types/vault.types';
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
  SidebarSeparator,
  useSidebar,
} from './ui/sidebar';

interface MenuItem {
  label: string;
  path: string;
  icon: React.ReactElement;
  count?: number;
}

interface MenuGroup {
  groupLabel: string;
  items: MenuItem[];
}

interface AppSidebarProps {
  vaultId?: string;
}

export const AppSidebar: React.FC<AppSidebarProps> = ({ vaultId }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { currentVault } = useAppSelector(state => state.vault);
  const { isMobile, setOpenMobile } = useSidebar();
  const isDebugMode = useDebugMode();

  // Extract vaultId from current path if not provided
  const currentVaultId = vaultId || location.pathname.split('/')[2];

  // Vault password dialog state
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [selectedVaultId, setSelectedVaultId] = useState<string>('');
  const [selectedVaultName, setSelectedVaultName] = useState<string>('');

  // Check if we're on a vault page
  const isVaultPage = location.pathname.startsWith('/vaults/');

  const activeCounts = currentVault?.data ? VaultDocument.countActive(currentVault.data) : null;
  const settings = currentVault?.data?.settings ?? VaultSettingsNode.createDefault();

  // Vault switcher handlers
  const handleVaultSelect = async (vaultId: string, vaultName: string) => {
    // If vault is already unlocked, navigate directly
    if (cryptographerService.hasCryptographer(vaultId)) {
      try {
        // Load the vault data
        const result = await dispatch(readVaultThunk(vaultId));
        
        if (readVaultThunk.fulfilled.match(result)) {
          // Navigate to the vault
          navigate(`/vaults/${vaultId}`);
        }
      } catch (error) {
        console.error('Failed to load vault:', error);
      }
      return;
    }
    
    // Otherwise, show password dialog
    setSelectedVaultId(vaultId);
    setSelectedVaultName(vaultName);
    setShowPasswordDialog(true);
    setPasswordError('');
  };

  const handlePasswordSuccess = async (vaultId: string, password: string) => {
    setPasswordLoading(true);
    setPasswordError('');
    
    try {
      // Initialize cryptographer for the vault
      await cryptographerService.setCryptographer(vaultId, password);
      
      // Load the vault data
      const result = await dispatch(readVaultThunk(vaultId));
      
      if (readVaultThunk.fulfilled.match(result)) {
        // Navigate to the new vault
        navigate(`/vaults/${vaultId}`);
        setShowPasswordDialog(false);
        setPasswordLoading(false);
      } else {
        throw new Error(result.error?.message || 'Failed to load vault');
      }
    } catch (error) {
      setPasswordError(error instanceof Error ? error.message : 'Invalid password or vault error');
      setPasswordLoading(false);
    }
  };

  const handlePasswordClose = () => {
    setShowPasswordDialog(false);
    setPasswordError('');
    setSelectedVaultId('');
    setSelectedVaultName('');
  };

  const vaultItemGroups: MenuGroup[] = currentVaultId ? [
    {
      groupLabel: 'Overview',
      items: [
        {
          label: 'Dashboard',
          path: `/vaults/${currentVaultId}`,
          icon: <DashboardIcon className="h-4 w-4" />
        },
        {
          label: 'People',
          path: `/vaults/${currentVaultId}/people`,
          icon: <PersonIcon className="h-4 w-4" />,
          count: activeCounts?.people ?? 0
        },
        ...(settings.enablePets ? [{
          label: 'Pets',
          path: `/vaults/${currentVaultId}/pets`,
          icon: <PetIcon className="h-4 w-4" />,
          count: activeCounts?.pets ?? 0
        }] : [])
      ]
    },
    {
      groupLabel: 'Assets',
      items: [
        ...(settings.enableVehicles ? [{
          label: 'Vehicles',
          path: `/vaults/${currentVaultId}/vehicles`,
          icon: <VehicleIcon className="h-4 w-4" />,
          count: activeCounts?.vehicles ?? 0
        }] : []),
        ...(settings.enableProperties ? [{
          label: 'Properties',
          path: `/vaults/${currentVaultId}/properties`,
          icon: <PropertyIcon className="h-4 w-4" />,
          count: activeCounts?.properties ?? 0
        }] : []),
        ...(settings.enableItems ? [{
          label: 'Items',
          path: `/vaults/${currentVaultId}/items`,
          icon: <ItemIcon className="h-4 w-4" />,
          count: activeCounts?.items ?? 0
        }] : [])
      ]
    },
    {
      groupLabel: 'Financial',
      items: [
        {
          label: 'Incomes',
          path: `/vaults/${currentVaultId}/incomes`,
          icon: <IncomesIcon className="h-4 w-4" />,
          count: activeCounts?.incomes ?? 0
        },
        {
          label: 'Expenditures',
          path: `/vaults/${currentVaultId}/expenditures`,
          icon: <ExpendituresIcon className="h-4 w-4" />,
          count: activeCounts?.expenditures ?? 0
        },
        ...(settings.enableSavings ? [{
          label: 'Savings',
          path: `/vaults/${currentVaultId}/savings`,
          icon: <SavingsIcon className="h-4 w-4" />,
          count: activeCounts?.savings ?? 0
        }] : []),
        ...(settings.enableProtection ? [{
          label: 'Protection',
          path: `/vaults/${currentVaultId}/protection`,
          icon: <ProtectionIcon className="h-4 w-4" />,
          count: activeCounts?.protection ?? 0
        }] : []),
        ...(settings.enableLiabilities ? [{
          label: 'Liabilities',
          path: `/vaults/${currentVaultId}/liabilities`,
          icon: <LiabilityIcon className="h-4 w-4" />,
          count: activeCounts?.liabilities ?? 0
        }] : [])
      ]
    },
    {
      groupLabel: 'Configuration',
      items: [
        {
          label: 'Settings',
          path: `/vaults/${currentVaultId}/settings`,
          icon: <SettingsIcon className="h-4 w-4" />
        },
        ...(isDebugMode ? [{
          label: 'Debug',
          path: `/vaults/${currentVaultId}/debug`,
          icon: <DebugIcon className="h-4 w-4" />
        }] : [])
      ]
    }
  ] : [];

  const isCurrentPath = (path: string) => {
    return location.pathname === path;
  };

  // Handle navigation click - close sidebar on mobile
  const handleNavigationClick = () => {
    if (isMobile) {
      setOpenMobile(false);
    }
    // Navigation will be handled by the Link component
  };

  return (
    <>
      <Sidebar variant="inset" side="right">
        <SidebarHeader>
          {isVaultPage && currentVault ? (
            <VaultSwitcher onVaultSelect={handleVaultSelect} size="default" />
          ) : (
            <h2 className="px-2 text-lg font-semibold tracking-tight">
              Navigation
            </h2>
          )}
        </SidebarHeader>
      
      <SidebarContent>

        {vaultItemGroups.length > 0 && (
          <>
            {vaultItemGroups.map((group, groupIndex) => (
              <React.Fragment key={groupIndex}>
                {group.items.length > 0 && (
                  <>
                    <SidebarSeparator className="mx-0!"/>
                    <SidebarGroup>
                      <SidebarGroupLabel>{group.groupLabel}</SidebarGroupLabel>
                      <SidebarGroupContent>
                        <SidebarMenu>
                          {group.items.map((item, itemIndex) => (
                            <SidebarMenuItem key={`${groupIndex}-${itemIndex}`}>
                              <SidebarMenuButton 
                                asChild 
                                isActive={isCurrentPath(item.path)}
                              >
                                <Link 
                                  to={item.path}
                                  onClick={() => handleNavigationClick()}
                                >
                                  {item.icon}
                                  <span>{item.label}</span>
                                </Link>
                              </SidebarMenuButton>
                              {item.count !== undefined && (
                                <SidebarMenuBadge>{item.count}</SidebarMenuBadge>
                              )}
                            </SidebarMenuItem>
                          ))}
                        </SidebarMenu>
                      </SidebarGroupContent>
                    </SidebarGroup>
                  </>
                )}
              </React.Fragment>
            ))}
          </>
        )}
      </SidebarContent>

      <SidebarRail />
    </Sidebar>

    {/* Vault Password Dialog */}
    <VaultPasswordDialog
      isOpen={showPasswordDialog}
      vaultName={selectedVaultName}
      vaultId={selectedVaultId}
      onClose={handlePasswordClose}
      onSuccess={handlePasswordSuccess}
      isLoading={passwordLoading}
      error={passwordError}
    />
    </>
  );
};

export default AppSidebar;